import { NavLink, Outlet, useLocation } from "react-router-dom";
import RouteSuspense from "./RouteSuspense";
import AppContextBar from "./AppContextBar";
import ProductSwitcher from "./ProductSwitcher";
import AccountMenu from "./AccountMenu";
import { getLastProduct } from "../../utils/lastProduct";
import { useMenuRoles } from "../auth/MenuRoleContext";
import { useLanguage } from "../ui/LanguageProvider";
import logoDefault from "../../image/colo/logo-default.png";
import logoWhite from "../../image/colo/logo-withe.png";
// 셸 공통 CSS(.app-shell · .page-body · .app-layout-shell)는 이 파일에서 로드 — TopNav 제거 후에도 유지.
import "./topNavStyle.css";

export default function PageLayout() {
  const { t } = useLanguage();
  const location = useLocation();
  const isEmbedded = new URLSearchParams(location.search).get("embedded") === "1";
  // CRM은 좌측 사이드바(CRMLayout)가 브랜드·검색·네비·계정/액션(footer)을 모두 담당 →
  // 전역 상단바(TopNav)를 렌더하지 않는다. 남는 높이는 app-shell--no-topnav 로 100vh 회수.
  const isCrm = location.pathname.startsWith("/crm");
  // IT-CONSULTING 영역 — AutomationLayout 좌사이드바가 자체 셸(스위처·검색·계정·상단바) 제공 → TopNav 불필요.
  const isItc = /^\/(automation|planning|settlement)(\/|$)/.test(location.pathname);
  // 설정 — SettingsLayout 좌사이드바가 자체 셸(스위처·메뉴·계정) 제공 → TopNav/얇은 바 불필요.
  const isSettings = location.pathname.startsWith("/settings");
  // 끝 슬래시(/home/)로 들어와도 전체화면 홈이어야 한다 — 일반 분기로 새면 상단바가 붙고,
  // 히어로 높이 계산(--nav-height)과 실제 바 높이가 어긋나 하단에 띠가 남는다.
  const isHome = /^\/home\/?$/.test(location.pathname);
  // 프린트/PDF 렌더 페이지 — 크롬(바) 없이 본문만.
  const isBare = /\/pdf(\/|$)/.test(location.pathname);
  const { visible } = useMenuRoles();

  if (isEmbedded) {
    return (
      <div className="app-shell app-shell--embedded">
        <style>{`
          .app-shell--embedded .page-body {
            padding: 0 !important;
            margin: 0 !important;
            min-height: 100vh !important;
          }
          .app-shell--embedded .quote-page-app > div:first-child,
          .app-shell--embedded .contract-page-app > div:first-child,
          .app-shell--embedded .invoice-page-app > div:first-child,
          .app-shell--embedded .other-quote-page-app > div:first-child,
          .app-shell--embedded .nda-app > div:first-child,
          .app-shell--embedded .standard-mou-app > div:first-child,
          .app-shell--embedded .maintenance-mou-app > div:first-child,
          .app-shell--embedded .order-approval-app > div:first-child {
            display: none !important;
          }
          .app-shell--embedded .quote-page-app > div:nth-child(2),
          .app-shell--embedded .contract-page-app > div:nth-child(2),
          .app-shell--embedded .invoice-page-app > div:nth-child(2),
          .app-shell--embedded .other-quote-page-app > div:nth-child(2),
          .app-shell--embedded .nda-app > div:nth-child(2),
          .app-shell--embedded .standard-mou-app > div:nth-child(2),
          .app-shell--embedded .maintenance-mou-app > div:nth-child(2),
          .app-shell--embedded .order-approval-app > div:nth-child(2) {
            padding: 12px 16px 24px !important;
            gap: 14px !important;
          }
        `}</style>
        <main className="page-body" key={location.pathname}>
          <RouteSuspense><Outlet /></RouteSuspense>
        </main>
      </div>
    );
  }

  // 앱 셸 영역(CRM · IT-CONSULTING · 설정) — 자체 좌사이드바가 전역 상단바를 대체. TopNav 미렌더 + 100vh 회수.
  if (isCrm || isItc || isSettings) {
    return (
      <div className="app-shell app-shell--no-topnav">
        <main className="page-body" key={location.pathname}>
          <RouteSuspense><Outlet /></RouteSuspense>
        </main>
      </div>
    );
  }

  // 홈(시네마틱 랜딩) — 상단바 없이 전체화면 + 좌상단 고정 '로고 + 앱으로 이동'(CRM 사이드바 로고와 같은 자리) 진입 CTA(마지막 제품).
  // 로고는 CRM 좌상단(ProductSwitcher)과 같은 것 — 어두운 배경이라 워드마크는 흰 판, 심볼만 원본 파랑을 겹친다.
  if (isHome) {
    return (
      <div className="app-shell app-shell--no-topnav">
        <NavLink to={getLastProduct(visible)} className="home-app-cta" aria-label={t("Colosseum Vault 앱으로 이동", "Go to the Colosseum Vault app")}>
          <span className="home-app-cta__logo" aria-hidden>
            <img src={logoWhite} alt="" />
            <span className="home-app-cta__mark" style={{ backgroundImage: `url(${logoDefault})` }} />
          </span>
          <span className="home-app-cta__label">{t("앱으로 이동", "Go to app")}</span>
        </NavLink>
        <main className="page-body" key={location.pathname}>
          <RouteSuspense><Outlet /></RouteSuspense>
        </main>
      </div>
    );
  }

  // 그 외(자체 네비 없는 유틸·공개 페이지: 설정·마케팅 카탈로그·진단·문의 등) — TopNav 대신
  // 로고 제품 스위처 + 계정을 담은 얇은 바로 이동/로그아웃 동선을 CRM·IT-CONSULTING과 동일하게 제공.
  return (
    <div className="app-shell">
      {!isBare ? (
        <AppContextBar left={<ProductSwitcher bar />} trailing={<AccountMenu variant="bar" />} />
      ) : null}
      <main className="page-body" key={location.pathname}>
        <RouteSuspense><Outlet /></RouteSuspense>
      </main>
    </div>
  );
}
